import { useMemo } from "react";
import Button from "../atoms/button";
import { buildCompareTableTurns, divergentFieldsForTurn } from "./compareTableModel";

const ROW_DEFS = [
    { key: "input", label: "Entrada", field: null },
    { key: "output", label: "Saída", field: "output" },
    { key: "pass", label: "pass", field: "pass" },
    { key: "intent", label: "intent", field: "intent" },
    { key: "handoff", label: "handoff", field: "handoff" },
    { key: "latency", label: "latência", field: "latency" },
    { key: "cost", label: "custo", field: "cost" },
    { key: "tokens", label: "tokens in/out", field: null },
];

function cellValue(col, rowKey) {
    switch (rowKey) {
        case "input":
            return col.input || "—";
        case "output":
            if (col.pending) return "digitando…";
            return col.output || "—";
        case "pass":
            if (col.pending) return "…";
            if (col.pass === true) return "✓";
            if (col.pass === false) return "✗";
            return "—";
        case "intent":
            return col.pending ? "…" : col.intent || "—";
        case "handoff":
            if (col.pending) return "…";
            if (col.handoff_now == null) return "—";
            return String(col.handoff_now);
        case "latency":
            return col.pending ? "…" : col.latencyLabel;
        case "cost":
            return col.pending ? "…" : col.costLabel;
        case "tokens":
            return col.pending ? "…" : col.tokensLabel;
        default:
            return "—";
    }
}

function cellClass(divergent, pending, rowKey) {
    const base = "align-top px-2 py-1.5 text-xs border-t border-gray-800";
    if (pending && (rowKey === "output" || rowKey === "pass")) {
        return `${base} text-gray-500 italic`;
    }
    if (divergent) {
        return `${base} bg-amber-950/50 text-amber-50 border-amber-800/40`;
    }
    return `${base} text-gray-200`;
}

export default function CompareTableModal({
    open,
    onClose,
    results = [],
    models = [],
    progress = null,
    suiteSnapshot = null,
    focusTurnId = null,
}) {
    const turns = useMemo(
        () =>
            buildCompareTableTurns({
                results,
                models,
                progress,
                suiteSnapshot,
            }),
        [results, models, progress, suiteSnapshot]
    );

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-3"
            role="dialog"
            aria-modal="true"
            aria-label="Tabela comparativa"
            data-testid="compare-table-modal"
        >
            <div className="w-full max-w-5xl rounded-xl border border-[#d59106]/50 bg-gray-900 p-4 shadow-xl max-h-[92vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-3 gap-2">
                    <h3 className="text-sm font-medium text-laranjinha">
                        Comparação lado a lado
                    </h3>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-gray-400 hover:text-white text-sm"
                        data-testid="compare-table-close"
                    >
                        Fechar
                    </button>
                </div>

                {turns.length === 0 ? (
                    <p className="text-sm text-gray-400">Aguardando turnos…</p>
                ) : (
                    <div className="space-y-5">
                        {turns.map((turn) => {
                            const divergent = divergentFieldsForTurn(turn);
                            const focused = focusTurnId && focusTurnId === turn.turnId;
                            return (
                                <section
                                    key={turn.turnId}
                                    data-testid={`compare-table-turn-${turn.turnId}`}
                                    className={
                                        focused
                                            ? "rounded-lg ring-1 ring-[#d59106]/60 p-2"
                                            : ""
                                    }
                                >
                                    <h4 className="text-xs font-medium text-laranjinha mb-2">
                                        Turno {turn.turnId}
                                        {turn.sharedInput ? (
                                            <span className="text-gray-400 font-normal ml-2">
                                                — {turn.sharedInput}
                                            </span>
                                        ) : null}
                                    </h4>
                                    <div className="overflow-x-auto">
                                        <table className="w-full min-w-[480px] border-collapse">
                                            <thead>
                                                <tr>
                                                    <th className="text-left text-[10px] uppercase tracking-wide text-gray-500 px-2 py-1 w-28">
                                                        Campo
                                                    </th>
                                                    {turn.columns.map((col) => (
                                                        <th
                                                            key={col.slot}
                                                            className="text-left text-xs text-laranjinha px-2 py-1"
                                                            data-testid={`compare-table-col-${turn.turnId}-${col.slot}`}
                                                        >
                                                            {col.label}
                                                            {col.pending ? (
                                                                <span className="ml-1 text-gray-500 font-normal">
                                                                    · ao vivo
                                                                </span>
                                                            ) : null}
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {ROW_DEFS.map((def) => (
                                                    <tr key={def.key}>
                                                        <th className="text-left text-[11px] text-gray-500 px-2 py-1.5 font-normal border-t border-gray-800">
                                                            {def.label}
                                                        </th>
                                                        {turn.columns.map((col) => {
                                                            const isDiv =
                                                                def.field &&
                                                                divergent.has(def.field);
                                                            const val = cellValue(col, def.key);
                                                            const isLong =
                                                                def.key === "output" ||
                                                                def.key === "input";
                                                            return (
                                                                <td
                                                                    key={col.slot}
                                                                    className={cellClass(
                                                                        isDiv,
                                                                        col.pending,
                                                                        def.key
                                                                    )}
                                                                    data-testid={`compare-table-cell-${turn.turnId}-${col.slot}-${def.key}`}
                                                                >
                                                                    {isLong ? (
                                                                        <pre className="whitespace-pre-wrap break-words font-sans m-0 max-h-40 overflow-y-auto">
                                                                            {val}
                                                                        </pre>
                                                                    ) : (
                                                                        val
                                                                    )}
                                                                </td>
                                                            );
                                                        })}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </section>
                            );
                        })}
                    </div>
                )}

                <div className="mt-4">
                    <Button
                        click={onClose}
                        classes="bg-gray-700 hover:bg-gray-600 px-3 py-1 text-white text-sm"
                    >
                        Fechar
                    </Button>
                </div>
            </div>
        </div>
    );
}
